import { css } from '@emotion/react'

import { MOBILE } from '../guided-toolbox/utils/breakpoints'

export const DEFAULT_BUTTON_WIDTH = '200px'

const buttonSizes = {
  medium: { minHeight: '50px', fontSize: '18px' },
  small: { minHeight: '40px', fontSize: '14px' },
  xsmall: { minHeight: '34px', fontSize: '14px', lineHeight: '18px', hugsLabel: true },
}

export const BUTTON_SIZES = Object.keys(buttonSizes)

export const buttonBase = css`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: 999px;
  user-select: none;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  transition:
    background-color 0.25s,
    border-color 0.25s,
    color 0.25s;

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: -4px;
  }
`

const hugsLabel = p => Boolean(buttonSizes[p.size]?.hugsLabel)

export const fillsMobileWidth = p => !hugsLabel(p) && p.width == null

export const sizeStyles = p => {
  const size = buttonSizes[p.size] ?? buttonSizes.medium
  const hasExplicitWidth = p.width != null
  const hugs = hugsLabel(p)

  const width = p.width ?? 'auto'
  const minWidth = hasExplicitWidth ? p.width : hugs ? '0' : DEFAULT_BUTTON_WIDTH
  const padding = hasExplicitWidth ? '0' : hugs ? '0 26px' : '0 30px'

  return css`
    width: ${width};
    min-width: ${minWidth};
    min-height: ${size.minHeight};
    padding: ${padding};
    font-size: ${size.fontSize};
    ${size.lineHeight ? `line-height: ${size.lineHeight};` : ''}
    font-weight: 600;

    ${MOBILE} {
      min-width: 0;
    }
  `
}

export const mobileFullWidth = css`
  ${MOBILE} {
    width: 100%;
  }
`

const primaryStyles = p => css`
  color: #ffffff;
  border: none;
  background: ${p.theme.primaryButtonColor ?? p.theme.primaryColor ?? '#038ab7'};

  &:disabled {
    background: ${p.theme.primaryButtonDisabledColor ?? '#d4ebf2'};
  }

  &:active:not(:disabled) {
    background: ${p.theme.primaryButtonActiveColor ?? '#02607f'};
  }

  &:hover:not(:disabled):not(:active) {
    background: ${p.theme.primaryButtonHoverColor ?? '#02769d'};
  }
`

const secondaryStyles = p => css`
  color: ${p.theme.addButtonTextColor ?? p.theme.mediumGray ?? '#4a606a'};
  background-color: ${p.theme.addButtonBgColor ?? '#ffffff'};
  border: ${p.theme.addButtonBorder ?? '1px solid #b6c0c4'};

  &:disabled {
    color: ${p.theme.addButtonDisabledTextColor ?? '#b6c0c4'};
    background-color: ${p.theme.addButtonDisabledColor ?? '#ffffff'};
    border: ${p.theme.addButtonDisabledBorder ?? '1px solid #dadfe1'};
  }

  &:active:not(:disabled) {
    color: ${p.theme.addButtonActiveTextColor ?? '#ffffff'};
    background-color: ${p.theme.addButtonActiveColor ?? '#9eacb0'};
    border: ${p.theme.addButtonActiveBorder ?? '1px solid rgba(122, 142, 150, 0.4)'};
  }

  &:hover:not(:disabled):not(:active) {
    color: ${p.theme.addButtonHoverTextColor ?? '#022a3a'};
    background-color: ${p.theme.addButtonHoverColor ?? '#f3f4f5'};
    border: ${p.theme.addButtonHoverBorder ?? '1px solid #b6c0c4'};
  }
`

const tertiaryStyles = p => css`
  color: #ffffff;
  border: none;
  background: ${p.theme.mediumGray ?? '#4a606a'};

  &:disabled {
    background: ${p.theme.lightestGray ?? '#7a8e96'};
  }

  &:active:not(:disabled) {
    background: #2f3f47;
  }

  &:hover:not(:disabled):not(:active) {
    background: #3a4d57;
  }
`

export const tertiaryLightStyles = p => css`
  color: #ffffff;
  border: none;
  background: ${p.theme.lightestGray ?? '#7a8e96'};

  &:disabled {
    background: #b6c0c4;
  }

  &:active:not(:disabled) {
    background: #55656c;
  }

  &:hover:not(:disabled):not(:active) {
    background: #65767e;
  }
`

const buttonVariants = {
  primary: primaryStyles,
  secondary: secondaryStyles,
  tertiary: tertiaryStyles,
  tertiaryLight: tertiaryLightStyles,
}

export const BUTTON_VARIANTS = Object.keys(buttonVariants)

export const variantStyles = p => buttonVariants[p.variant]?.(p)
